import { useVeloraStore } from '../store/useVeloraStore';
import { useTheme } from '../hooks/useTheme';

const ACH_DESC: Record<string, string> = {
  first_session:    'Complete your first session',
  streak_7:         'Train 7 days in a row',
  streak_30:        'Train 30 days in a row',
  notes_100:        'Hit 100 notes in tune',
  notes_1000:       'Hit 1000 notes in tune total',
  full_octave:      'Sing a full octave range',
  perfect_session:  'Score 90%+ in a session',
  range_test:       'Complete vocal range test',
};

export default function Achievements() {
  const theme = useTheme();
  const { achievements, currentStreak, sessionCount, allTimeNotesInTune, voiceType } = useVeloraStore();
  const unlocked = achievements.filter(a => a.unlockedAt).length;

  return (
    <div className="flex flex-col min-h-screen pb-24" style={{ background: 'transparent' }}>

      {/* Header */}
      <div className="safe-top px-5 pt-4 pb-5">
        <p className="text-xs tracking-widest uppercase font-medium mb-1"
          style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Achievements</p>
        <h1 className="text-2xl font-bold" style={{ color: theme.text, fontFamily: 'Syne, sans-serif' }}>
          PROGRESS
        </h1>
        <div className="flex items-center gap-2 mt-2">
          <div className="glass-pill px-3 py-1 text-xs font-semibold"
            style={{ color: theme.accent, fontFamily: 'Syne, sans-serif' }}>
            {unlocked}/{achievements.length} unlocked
          </div>
          <div className="flex-1 h-1.5 glass-card rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${(unlocked / achievements.length) * 100}%`,
                background: `linear-gradient(90deg, ${theme.primary}, ${theme.light})`,
                boxShadow: `0 0 8px ${theme.accent}60`,
              }} />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 mb-4 grid grid-cols-3 gap-2.5">
        {[
          { label: 'Sessions', value: sessionCount, icon: '🎤' },
          { label: 'Day Streak', value: currentStreak, icon: '🔥' },
          { label: 'Notes Sung', value: allTimeNotesInTune, icon: '🎵' },
        ].map(s => (
          <div key={s.label} className="glass-card rounded-2xl p-3.5 text-center">
            <div className="text-xl">{s.icon}</div>
            <div className="text-lg font-bold font-mono" style={{ color: theme.accent }}>{s.value}</div>
            <div className="text-xs mt-0.5" style={{ color: theme.muted }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Voice type */}
      {voiceType && (
        <div className="mx-4 mb-4 glass-card rounded-2xl p-4 flex items-center gap-3">
          <span className="text-3xl">🎭</span>
          <div>
            <p className="text-xs tracking-widest uppercase" style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>
              Voice Type
            </p>
            <p className="text-lg font-bold" style={{ color: theme.accent, fontFamily: 'Syne, sans-serif' }}>
              {voiceType}
            </p>
          </div>
        </div>
      )}

      {/* Achievement grid */}
      <div className="px-4 grid grid-cols-2 gap-3">
        {achievements.map((a, i) => (
          <div key={a.id}
            className="glass-card rounded-2xl p-4 flex flex-col items-center gap-2 text-center fade-up"
            style={{
              animationDelay: `${i * 0.04}s`,
              background: a.unlockedAt ? 'rgba(83,74,183,0.15)' : undefined,
              borderColor: a.unlockedAt ? 'rgba(127,119,221,0.40)' : undefined,
              opacity: a.unlockedAt ? 1 : 0.45,
              boxShadow: a.unlockedAt ? `0 4px 20px rgba(83,74,183,0.2), inset 0 1px 0 rgba(255,255,255,0.12)` : undefined,
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={e => { if (a.unlockedAt) (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.transform = ''; }}
          >
            <span className="text-3xl" style={{ filter: a.unlockedAt ? 'none' : 'grayscale(1) opacity(0.4)' }}>
              {a.unlockedAt ? a.icon : '🔒'}
            </span>
            <p className="text-xs font-semibold" style={{ color: a.unlockedAt ? theme.text : theme.dim, fontFamily: 'Syne, sans-serif' }}>
              {a.label}
            </p>
            <p className="text-xs leading-snug" style={{ color: a.unlockedAt ? theme.muted : theme.dim }}>
              {a.unlockedAt ? new Date(a.unlockedAt).toLocaleDateString() : (ACH_DESC[a.id] ?? a.label)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
